import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { isDataSourceConfigured } from '../utils/dataSourceHelpers';
import { MobileUserService } from '../api/generated/services/MobileUserService';
import { MobileShopService } from '../api/generated/services/MobileShopService';
import type { ShopView } from '../api/generated/models/ShopView';
import type { StatisticsSummaryView } from '../api/generated/models/StatisticsSummaryView';
import { aggregateSummaries, getDisplayCurrency } from '../utils/statisticsHelpers';
import { ApiError } from '../api/generated/core/ApiError';
import {
  StoreStatisticsWidget,
  OrdersWidget,
  SalesByPaymentMethodsWidget,
  SalesByCustomerGroupWidget,
  ConversionWidget,
  TopsellerWidget,
  AverageCheckWidget,
  // AbandonedCartsWidget,
  SalesStrengthByHoursWidget,
  WawiSalesOverviewWidget,
} from '../components/dashboard';
import { PeriodFilterDropdown, PeriodFilterValue } from '../components/dashboard/PeriodFilterDropdown';
import WidgetUpgradeCTA from '../components/WidgetUpgradeCTA';
import { useAccountFeatures } from '../hooks/useLicenseCheck';
import { usePermissions } from '../hooks/usePermissions';

function periodValueToFilters(pv: PeriodFilterValue): Array<{ key: 'period' | 'startDate' | 'endDate' | 'limit'; value: string }> {
  const filters: Array<{ key: 'period' | 'startDate' | 'endDate' | 'limit'; value: string }> = [
    { key: 'limit', value: '20' },
  ];
  if (pv.preset) {
    const presetToDays: Record<string, string> = {
      today: '1',
      '7': '7',
      '30': '30',
    };
    if (presetToDays[pv.preset]) {
      filters.push({ key: 'period', value: presetToDays[pv.preset] });
    } else {
      const now = new Date();
      let start: Date;
      let end: Date = now;
      switch (pv.preset) {
        case 'this_month':
          start = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case 'last_month':
          start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          end = new Date(now.getFullYear(), now.getMonth(), 0);
          break;
        case 'this_year':
          start = new Date(now.getFullYear(), 0, 1);
          break;
        default:
          start = new Date(now.getFullYear(), now.getMonth(), 1);
      }
      const fmt = (d: Date) => d.toISOString().slice(0, 10);
      filters.push({ key: 'startDate', value: fmt(start) });
      filters.push({ key: 'endDate', value: fmt(end) });
    }
  } else if (pv.startDate && pv.endDate) {
    filters.push({ key: 'startDate', value: pv.startDate });
    filters.push({ key: 'endDate', value: pv.endDate });
  } else {
    filters.push({ key: 'period', value: '30' });
  }
  return filters;
}

const SHOP_TYPE_LOGOS: Record<string, string> = {
  jtl_shop_5: require('../assets/icons/logos/jtl.ico'),
  jtl_wawi_1_5: require('../assets/icons/logos/jtl.ico'),
  jtl_wawi_1_6: require('../assets/icons/logos/jtl.ico'),
  shopify_shop: require('../assets/icons/logos/shopify.ico'),
  // ebay_shop: require('../assets/icons/logos/ebay.png'),
  // amazon_shop: require('../assets/icons/logos/amazon.png'),
};

function ShopTypeIcon({ shopType }: { shopType: string }) {
  const src = SHOP_TYPE_LOGOS[shopType];
  if (!src) return null;
  return <img className="store-tab-icon" src={src} alt="" width="16" height="16" />;
}

export const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const dataSource = useSelector((state: RootState) => state.dataSource);
  const { showDashboard } = useAccountFeatures();
  const { can } = usePermissions();
  // Subuser widget permissions - independent of mobile_read (plugin access):
  // without the key the widget is hidden and its endpoint is never called.
  const canStatisticsSummary = can('mobile_statistics_summary_read');
  const canWawiSalesOverview = can('mobile_sales_overview_read');
  const showWawiSalesOverview = isDataSourceConfigured(dataSource.data);
  const [shops, setShops] = useState<ShopView[]>([]);
  const [shopsLoading, setShopsLoading] = useState(true);
  const [licenseRequired, setLicenseRequired] = useState(false);
  const [selectedStoreIndex, setSelectedStoreIndex] = useState<number>(0);
  const [periodValue, setPeriodValue] = useState<PeriodFilterValue>({ preset: '30', startDate: null, endDate: null });
  const [summary, setSummary] = useState<StatisticsSummaryView | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  /**
   * `true` when statistics-summary answered 400 ("Error (e.g. shop not found)"):
   * the registered shop is not usable yet, so the add-shop card is shown
   * instead of an error banner.
   */
  const [shopNotFound, setShopNotFound] = useState(false);
  const [failedShopIds, setFailedShopIds] = useState<Set<string>>(new Set());
  const [loadedCount, setLoadedCount] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    MobileUserService.getAppMobileUserShopList()
      .then((list) => {
        setShops(list ?? []);
        if (selectedStoreIndex >= (list?.length ?? 0)) {
          setSelectedStoreIndex(0);
        }
      })
      .catch((err) => {
        if (err instanceof ApiError) {
          const body = err.body;
          const parsed = typeof body === 'string' ? (() => { try { return JSON.parse(body); } catch { return null; } })() : body;
          if (parsed?.error === 'license_required') {
            setLicenseRequired(true);
            setShops([]);
            return;
          }
        }
        setShops([]);
      })
      .finally(() => setShopsLoading(false));
  }, []);

  const effectiveShopIdForRequest =
    selectedStoreIndex === 0 ? (shops[0]?.shopId ?? null) : (shops[selectedStoreIndex - 1]?.shopId ?? null);
  const effectiveShopIdsForOrders = useMemo(() => {
    if (selectedStoreIndex === 0) return shops.map((s) => s.shopId);
    const id = shops[selectedStoreIndex - 1]?.shopId;
    return id ? [id] : [];
  }, [shops, selectedStoreIndex]);
  const effectiveShopHost =
    selectedStoreIndex === 0 ? (shops[0]?.host ?? null) : (shops[selectedStoreIndex - 1]?.host ?? null);

  const fetchSummary = useCallback(() => {
    // No permission for the statistics endpoint - never call it.
    if (!canStatisticsSummary) {
      return;
    }
    if (effectiveShopIdForRequest == null) {
      if (shops.length === 0) {
        setSummary(null);
        setSummaryError(null);
        return;
      }
    }

    const filters = periodValueToFilters(periodValue);

    if (selectedStoreIndex === 0 && shops.length > 1) {
      setSummaryLoading(true);
      setSummaryError(null);
      setShopNotFound(false);
      setFailedShopIds(new Set());
      setLoadedCount(0);
      const tracked = shops.map((s) =>
        MobileShopService.postAppMobileShopStatisticsSummary(s.shopId, filters)
          .then((v) => { setLoadedCount((c) => c + 1); return v; })
          .catch((e) => { setLoadedCount((c) => c + 1); throw e; })
      );
      Promise.allSettled(tracked)
        .then((results) => {
          const succeeded: StatisticsSummaryView[] = [];
          const failed = new Set<string>();
          results.forEach((r, i) => {
            if (r.status === 'fulfilled') {
              succeeded.push(r.value);
            } else {
              failed.add(shops[i].shopId);
            }
          });
          setFailedShopIds(failed);
          if (succeeded.length > 0) {
            const currency = getDisplayCurrency(succeeded[0]);
            setSummary(aggregateSummaries(succeeded, currency));
          } else {
            setSummary(null);
            setSummaryError(t('dashboard_stats_all_shops_failed'));
          }
        })
        .finally(() => setSummaryLoading(false));
      return;
    }

    if (effectiveShopIdForRequest == null) {
      setSummary(null);
      return;
    }

    setSummaryLoading(true);
    setSummaryError(null);
    setShopNotFound(false);
    setFailedShopIds(new Set());
    MobileShopService.postAppMobileShopStatisticsSummary(effectiveShopIdForRequest, filters)
      .then(setSummary)
      .catch((err) => {
        setSummary(null);
        // 400 = "Error (e.g. shop not found)": show the add-shop card instead
        // of the error banner.
        if (err instanceof ApiError && err.status === 400) {
          setShopNotFound(true);
          return;
        }
        setSummaryError(err instanceof ApiError ? (err.message || String(err.body)) : String(err));
      })
      .finally(() => setSummaryLoading(false));
  }, [effectiveShopIdForRequest, periodValue, selectedStoreIndex, shops, canStatisticsSummary]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  if (shopsLoading) {
    return (
      <div className="dashboard-stats">
        <div className="dashboard-loading">
          <div className="order-sidebar-spinner" />
        </div>
      </div>
    );
  }

  if (licenseRequired) {
    return (
      <div className="dashboard-empty-state">
        <p className="dashboard-empty-state-text">{t('dashboard_license_required_title')}</p>
        <p className="dashboard-empty-state-hint">{t('dashboard_license_required_description')}</p>
        <Link to="/features/mobile-app" className="dashboard-empty-state-btn">
          {t('dashboard_license_required_button')}
        </Link>
      </div>
    );
  }

  return (
    <div className="dashboard-stats">
      <div className="store-tabs-row">
        <div className="store-tabs">
          <button
            type="button"
            className={`store-tab ${selectedStoreIndex === 0 ? 'active' : ''}`}
            onClick={() => setSelectedStoreIndex(0)}
          >
            {t('dashboard_stats_all_stores')}
          </button>
          {shops.map((shop, i) => {
            const label = (() => {
              if (!shop.host) return t('dashboard_stats_site', { n: i + 1 });
              const h = shop.host.trim();
              if (h.startsWith('http://') || h.startsWith('https://')) {
                try {
                  return new URL(h).hostname || h;
                } catch {
                  return h;
                }
              }
              return h;
            })();
            return (
              <button
                key={shop.shopId}
                type="button"
                className={`store-tab ${selectedStoreIndex === i + 1 ? 'active' : ''} ${failedShopIds.has(shop.shopId) ? 'store-tab--error' : ''}`}
                onClick={() => setSelectedStoreIndex(i + 1)}
                title={failedShopIds.has(shop.shopId) ? t('dashboard_stats_shop_request_failed') : undefined}
              >
                <ShopTypeIcon shopType={shop.shopType} />
                {label}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          className="store-tabs-refresh"
          onClick={() => { setRefreshKey((k) => k + 1); fetchSummary(); }}
          disabled={summaryLoading}
          title={t('dashboard_stats_refresh')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
        </button>
      </div>
      <div className="period-filter">
        <label>{t('dashboard_stats_period')}</label>
        <PeriodFilterDropdown value={periodValue} onChange={setPeriodValue} />
      </div>
      {summaryError && (
        <div className="dashboard-error">
          {summaryError}
        </div>
      )}
      {summaryLoading && (
        <div className="dashboard-loading-overlay">
          <div className="dashboard-loading">
            <div className="order-sidebar-spinner" />
            {shops.length > 1 && selectedStoreIndex === 0 && (
              <span className="dashboard-loading-counter">
                {t('dashboard_stats_loaded_count', { loaded: loadedCount, total: shops.length })}
              </span>
            )}
          </div>
        </div>
      )}
      <div className={`stats-grid ${summaryLoading ? 'stats-grid--loading' : ''}`}>
        {/* Wawi sales-overview ("Dashboard") is gated by the mobile plan's
            `jtl_wawi_enabled` flag. On Free mobile the flag is off: render the
            Upgrade CTA instead and never call the gated endpoint. On a paid
            plan, keep the existing data-source prerequisite. */}
        {canWawiSalesOverview &&
          (showDashboard ? (
            showWawiSalesOverview && (
              <WawiSalesOverviewWidget periodValue={periodValue} refreshKey={refreshKey} />
            )
          ) : (
            <WidgetUpgradeCTA className="stat-card" />
          ))}
        {/* No usable shop (none registered, or statistics answered 400
            "shop not found"): a stat-card-sized placeholder with an "add shop"
            CTA in place of the statistics widgets (the Wawi widget above still
            renders). */}
        {(shops.length === 0 || shopNotFound) && (
          <div className="stat-card dashboard-no-shops-card">
            <p className="dashboard-empty-state-text">{t('dashboard_stats_no_shops')}</p>
            <p className="dashboard-empty-state-hint">{t('dashboard_stats_no_shops_hint')}</p>
            <Link to="/features/mobile-app" className="dashboard-empty-state-btn">
              {t('dashboard_stats_add_shop_button')}
            </Link>
          </div>
        )}
        {summary && (
          <>
            <StoreStatisticsWidget data={summary} />
            <OrdersWidget shopIds={effectiveShopIdsForOrders} periodValue={periodValue} refreshKey={refreshKey} />
            <div className="stat-card-combined">
              <SalesByPaymentMethodsWidget data={summary} />
              <SalesByCustomerGroupWidget data={summary} />
            </div>
            <ConversionWidget data={summary} />
            <TopsellerWidget data={summary} shopHost={effectiveShopHost} />
            <AverageCheckWidget data={summary} />
            {/* <AbandonedCartsWidget data={summary} /> */}
            <SalesStrengthByHoursWidget data={summary} />
          </>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
